import { Box, Tab, Tabs, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaInfoCircle } from 'react-icons/fa'
import { useLocation, useNavigate } from 'react-router-dom'

import navbar from '@/assets/data/navbar'
import Tooltip from '@/components/Tooltip'

const WorkFlowTab = ({ width }: { width: number }) => {
  const [value, setValue] = useState(0)
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const recipes = useMemo(
    () =>
      navbar.wizards?.find((r) => r.id === location.pathname?.split('/')?.[1]),
    [location],
  )

  const steps = useMemo(
    () =>
      recipes?.children?.map((r) => ({
        title: t(`Navbar.nestedMenu.${r.key}`),
        path: r.route,
        tooltip: t(`Navbar.tooltip.nestedMenuTooltip.${r.key}`),
      })),
    [recipes, t],
  )

  useEffect(() => {
    const currentIndex = steps?.findIndex(
      (step) => location.pathname === step.path,
    )
    if (currentIndex !== -1 && currentIndex !== value) {
      setValue(currentIndex as number)
    }
  }, [location.pathname, steps])

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
    if (steps && steps[newValue]) {
      navigate({
        pathname: steps[newValue].path,
        search: location.search,
      })
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: width,
      }}
    >
      <Tabs
        value={value}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 'auto',
          height: '37px',
          '& .MuiTabs-indicator': { backgroundColor: 'transparent' },
          '& .MuiTab-root': {
            minHeight: 'auto !important',
            height: '37px',
            padding: '8px 10px',
          },
          '& .MuiTabs-scroller': { minHeight: 'auto', height: '37px' },
        }}
      >
        {steps?.map(({ title, tooltip, path }, index) => (
          <Tab
            key={path}
            disableRipple
            label={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: value === index ? 'white' : '#A0A0A0',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontFamily: 'Inter',
                      lineHeight: '21px',
                    }}
                  >
                    {index + 1}. {title}
                  </Typography>
                  <Tooltip
                    text={tooltip}
                    icon={
                      <FaInfoCircle
                        size={12}
                        style={{ width: '12px', height: '12px' }}
                      />
                    }
                    sx={{ ml: 0 }}
                    placement="bottom"
                  />
                </Box>
              </Box>
            }
            sx={{
              minHeight: 'auto',
              bgcolor:
                value === index ? 'rgba(144, 202, 249, 0.50)' : '#2C2C2C',
              borderRight:
                index === steps.length - 1 ? 'none' : '1px solid #3B5265',
              color: value !== index ? '#9CA3AF' : 'white',
              borderTopLeftRadius: index === 0 ? '8px' : 0,
              borderTopRightRadius: index === steps.length - 1 ? '8px' : 0,
              padding: '8px 10px',
            }}
          />
        ))}
      </Tabs>
    </Box>
  )
}

export default WorkFlowTab
